/**
 * Guarda de permiso para las pantallas de Equipo (Usuarios y Roles).
 *
 * La ruta ya cuelga de `RequireAuth`; el gating de permiso lo hace la pantalla
 * (como `OrderNewScreen` con `add_orders` o `RequireManageTalonarios`). Replica la
 * paridad con el SSR (`docs/frontend_architecture.md`, Pr. 5): sin el permiso
 * exigido se muestra un aviso en vez del contenido. Genérica para reutilizarla con
 * `manage_users` (usuarios) y `manage_roles` (roles).
 */
import type { ReactNode } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui";
import { useAuth } from "@/lib/auth";

interface RequirePermissionProps {
  /** Permiso exigido (mismo que la API y el menú del SSR). */
  permission: string;
  /** Título del aviso de "sin permiso". */
  title: string;
  /** Mensaje del aviso de "sin permiso". */
  deniedMessage: string;
  children: ReactNode;
}

export function RequirePermission({
  permission,
  title,
  deniedMessage,
  children,
}: RequirePermissionProps) {
  const { status, hasPermission } = useAuth();

  // Mientras la sesión se hidrata (`/auth/me`), `hasPermission` aún no conoce los
  // permisos: mostrar el aviso "sin permiso" en ese instante sería engañoso. Se
  // espera a tener la sesión resuelta (la ruta ya está protegida por `RequireAuth`).
  if (status === "loading") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500" role="status">
            Cargando…
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!hasPermission(permission)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-600" role="alert">
            {deniedMessage}
          </p>
        </CardContent>
      </Card>
    );
  }

  return <>{children}</>;
}
